function ensureCardRoot() {
  return document.body || document.documentElement;
}

/**
 * Show a Dead Cells-inspired enemy encounter card overlay.
 * @param {object} enemyData
 * @param {() => void} onDismiss
 * @param {{ accentColor?: string, alwaysShow?: boolean, phaseBadge?: string }} [options]
 */
export function showEnemyCard(enemyData, onDismiss, options = {}) {
  const root = ensureCardRoot();
  if (!root || !enemyData) return () => {};

  const existing = document.getElementById('tutorial-card');
  if (existing) existing.remove();

  const accentColor = options.accentColor || 'var(--accent)';
  const overlay = document.createElement('div');
  overlay.id = 'tutorial-card';
  overlay.className = 'enemy-card-overlay';
  overlay.style.setProperty('--enemy-card-accent', accentColor);

  const card = document.createElement('div');
  card.className = 'enemy-card-panel';
  overlay.appendChild(card);

  const left = document.createElement('div');
  left.className = 'enemy-card-left';
  card.appendChild(left);

  const img = document.createElement('img');
  img.className = 'enemy-card-image';
  img.alt = `${enemyData.displayName || enemyData.id || 'Enemy'} portrait`;
  img.src = enemyData.cardImage || '';
  left.appendChild(img);

  const fallback = document.createElement('div');
  fallback.className = 'enemy-card-silhouette';
  fallback.hidden = true;
  fallback.textContent = (enemyData.displayName || enemyData.id || '?').slice(0, 1).toUpperCase();
  left.appendChild(fallback);

  const right = document.createElement('div');
  right.className = 'enemy-card-right';
  card.appendChild(right);

  const header = document.createElement('div');
  header.className = 'enemy-card-header';
  right.appendChild(header);

  const title = document.createElement('h2');
  title.className = 'enemy-card-title';
  title.textContent = enemyData.displayName || enemyData.id || 'Unknown Enemy';
  header.appendChild(title);

  if (options.phaseBadge) {
    const badge = document.createElement('span');
    badge.className = 'enemy-card-badge';
    badge.textContent = options.phaseBadge;
    header.appendChild(badge);
  }

  const threat = document.createElement('div');
  threat.className = 'enemy-card-threat';
  const level = Math.max(1, Math.min(3, Number(enemyData.threatLevel) || 1));
  threat.textContent = `${'☠ '.repeat(level).trim()}  THREAT ${level}`;
  right.appendChild(threat);

  const abilities = document.createElement('div');
  abilities.className = 'enemy-card-abilities';
  for (const entry of enemyData.abilities || []) {
    const row = document.createElement('div');
    row.className = 'enemy-card-ability';
    row.innerHTML = `
      <span class="enemy-card-ability-icon">${entry.icon || '◆'}</span>
      <div class="enemy-card-ability-copy">
        <div class="enemy-card-ability-label">${entry.label || 'Ability'}</div>
        <div class="enemy-card-ability-desc">${entry.desc || ''}</div>
      </div>
    `;
    abilities.appendChild(row);
  }
  right.appendChild(abilities);

  const tip = document.createElement('p');
  tip.className = 'enemy-card-tip';
  tip.textContent = `⚠ Tip: ${enemyData.tip || 'Stay alert.'}`;
  right.appendChild(tip);

  const actionRow = document.createElement('div');
  actionRow.className = 'enemy-card-actions';
  const dismissBtn = document.createElement('button');
  dismissBtn.type = 'button';
  dismissBtn.className = 'enemy-card-dismiss primary';
  dismissBtn.textContent = 'Got it';
  actionRow.appendChild(dismissBtn);
  right.appendChild(actionRow);

  let dismissed = false;
  const cleanup = () => {
    overlay.removeEventListener('keydown', onKeyDown);
    dismissBtn.removeEventListener('click', dismiss);
    overlay.remove();
  };
  const finish = () => {
    if (dismissed) return;
    dismissed = true;
    cleanup();
    onDismiss?.();
  };
  const dismiss = () => {
    if (dismissed) return;
    overlay.classList.remove('is-visible');
    overlay.classList.add('is-dismissing');
    window.setTimeout(finish, 150);
  };
  const onKeyDown = (ev) => {
    if (ev.code === 'Space' || ev.code === 'Enter') {
      ev.preventDefault();
      dismiss();
    }
  };

  img.addEventListener('error', () => {
    img.hidden = true;
    fallback.hidden = false;
  });
  dismissBtn.addEventListener('click', dismiss);
  overlay.addEventListener('keydown', onKeyDown);
  overlay.tabIndex = -1;

  root.appendChild(overlay);
  requestAnimationFrame(() => {
    overlay.classList.add('is-visible');
    dismissBtn.focus({ preventScroll: true });
  });

  return dismiss;
}
